import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, take } from 'rxjs'
import { environment } from 'src/environments/environment'
import { AbstractChatService } from '../abstract-chat/abstract-chat.service'
import { AuthService } from '../auth/auth.service'
import { ChatRoomUnreadInfo } from './chat-unread-message.fixtures'

const UNREAD_MESSAGES_KEY = 'unreadMessages'

@Injectable({
  providedIn: 'root',
})
export class ChatUnreadMessageService extends AbstractChatService {
  readonly chatRoomsUnreadMessages: BehaviorSubject<ChatRoomUnreadInfo[]> =
    new BehaviorSubject([])

  constructor(
    private httpClient: HttpClient,
    authService: AuthService
  ) {
    super(authService)
  }

  override didFetchUserData(): void {
    this.fetchUnreadMessagesCount()
  }

  fetchUnreadMessagesCount() {
    if (!this.chatUserData) {
      return
    }

    const url = environment.chatBasePath + '/subscriptions.get'

    this.httpClient
      .get<any>(url, { headers: this.getAuthHeaders() })
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response && response.update) {
            const timestamp = new Date().getTime()
            const unreadMessages: ChatRoomUnreadInfo[] =
              response.update.flatMap((subscription: any) => {
                if (subscription?.rid) {
                  return {
                    chatRoomId: subscription?.rid,
                    unreadMessagesCount: subscription?.unread ?? 0,
                    timestamp,
                  }
                }
                return undefined
              })
            this.chatRoomsUnreadMessages.next(unreadMessages)
          }
        },
      })
  }

  addUnreadRoomInfo(roomId: string, unreadMessagesCount: number = 1) {
    const unreadMessages = this.chatRoomsUnreadMessages.value
    const newUnreadMessages = unreadMessages.filter(
      (unreadMessage) => unreadMessage.chatRoomId !== roomId
    )
    newUnreadMessages.push({
      chatRoomId: roomId,
      unreadMessagesCount,
      timestamp: new Date().getTime(),
    })

    this.chatRoomsUnreadMessages.next(newUnreadMessages)
  }

  clearMessages(roomId: string) {
    if (!this.chatUserData) {
      return
    }

    const url = environment.chatBasePath + '/subscriptions.read'
    const postData = {
      rid: roomId,
    }

    this.httpClient
      .post<any>(url, postData, { headers: this.getAuthHeaders() })
      .pipe(take(1))
      .subscribe()

    // clear local storage
    const unreadMessages = this.chatRoomsUnreadMessages.value
    const newUnreadMessages = unreadMessages.filter(
      (unreadMessage) => unreadMessage.chatRoomId !== roomId
    )
    this.chatRoomsUnreadMessages.next(newUnreadMessages)
  }

  private setUnreadMessagesCount(unreadMessages: ChatRoomUnreadInfo[]) {
    localStorage.setItem(UNREAD_MESSAGES_KEY, JSON.stringify(unreadMessages))
  }

  private getUnreadMessagesCount(): ChatRoomUnreadInfo[] {
    const unreadMessages = localStorage.getItem(UNREAD_MESSAGES_KEY)
    return unreadMessages
      ? (JSON.parse(unreadMessages) as ChatRoomUnreadInfo[])
      : []
  }
}
